import React, { useContext } from "react"
import { useWatch } from "react-hook-form"

import { SelectOption } from "shared/types/Select"
import { InputContext } from "shared/ui/input/Input"

import { SelectValue as ComboboxSelectValue } from "../select/components/SelectValue"

interface ReadonlyProps<T>
  extends Omit<
    React.PropsWithoutRef<React.HTMLProps<HTMLDivElement>>,
    "value"
  > {
  items?: SelectOption<T>[]
  value?: T | T[] | SelectOption<T> | SelectOption<T>[] | null | undefined
  render?: (
    value: T | T[] | SelectOption<T> | SelectOption<T>[] | null | undefined
  ) => React.ReactNode
  showAlways?: boolean
}

function ReadonlyComboboxBase<T>({
  className,
  children,
  render,
  items,
  value,
  showAlways,
  ...props
}: ReadonlyProps<T>) {
  const { readonly, loading } = useContext(InputContext)

  if ((!readonly && !showAlways) || loading) {
    return null
  }

  if (children) {
    return <div className="input__value input__value--readonly">{children}</div>
  }

  if (render) {
    return render(value)
  }

  return (
    <ComboboxSelectValue<T>
      {...props}
      value={value}
      options={items}
      className="input__value--readonly"
    />
  )
}

function HookFormReadonlyCombobox<T>(props: ReadonlyProps<T>) {
  const { name } = useContext(InputContext)
  const value = useWatch({ name }) as
    | T
    | T[]
    | SelectOption<T>
    | SelectOption<T>[]
    | null
    | undefined

  return <ReadonlyComboboxBase {...props} value={value} />
}

function ReadonlyComboboxComponent<T>(props: ReadonlyProps<T>) {
  const { name } = useContext(InputContext)

  if (name) {
    return <HookFormReadonlyCombobox<T> {...props} />
  }
  return <ReadonlyComboboxBase<T> {...props} />
}

export const ReadonlyCombobox = React.memo(
  ReadonlyComboboxComponent
) as typeof ReadonlyComboboxComponent
